import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, ArrowRight, CheckCircle, ShoppingBag, Users, 
  Building2, Store, Eye, TrendingDown, Zap, Shield,
  Smartphone, Palette, ChevronRight, Play
} from 'lucide-react';
import MarketingLayout from '../../components/MarketingLayout';
import SEO, { SEO_CONFIGS } from '../../components/SEO';
import { TRYON_PAGE_CONFIG } from '../../config/marketingConfig';

const TryOnPage = ({ config = TRYON_PAGE_CONFIG }) => {
  const [animationStep, setAnimationStep] = useState(0);
  const [isInView, setIsInView] = useState(false);
  const demoRef = useRef(null);

  // Animation steps: 0=human, 1=dress, 2=processing, 3=output
  useEffect(() => {
    if (!isInView) return;
    
    const interval = setInterval(() => {
      setAnimationStep((prev) => (prev + 1) % 4);
    }, 2500); // Each step ~2.5s, full loop ~10s

    return () => clearInterval(interval);
  }, [isInView]);

  // Intersection observer for demo section
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0.3 }
    );
    if (demoRef.current) observer.observe(demoRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <MarketingLayout>
      <SEO {...SEO_CONFIGS.tryon} />
      
      {/* Try-On Animation Demo Section */}
      <section ref={demoRef} className="pt-24 pb-16 sm:pt-32 sm:pb-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              How Virtual Try-On Works
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Watch how a customer photo transforms into a realistic try-on preview
            </p>
          </motion.div>

          {/* Desktop: 3-column, Mobile: stacked */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-4 items-start max-w-4xl mx-auto">
            {/* Human Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className={`relative rounded-2xl overflow-hidden bg-slate-100 aspect-[9/16] transition-all duration-700 ${animationStep >= 0 ? 'opacity-100' : 'opacity-30'}`}>
                <img
                  src={config.demo.humanImage}
                  alt="Customer Photo"
                  loading="lazy"
                  className="w-full h-full object-contain bg-slate-50"
                />
                <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/40 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/90 backdrop-blur rounded-full text-sm font-medium text-slate-700">
                    <Users className="w-4 h-4 text-blue-600" />
                    Customer Photo
                  </span>
                </div>
              </div>
              {animationStep === 0 && (
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="absolute -top-2 -right-2 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center"
                >
                  <span className="text-white text-sm font-bold">1</span>
                </motion.div>
              )}
            </motion.div>

            {/* Plus Sign & Dress Image */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              {/* Plus icon - hidden on mobile */}
              <div className="hidden md:flex absolute -left-8 top-1/2 -translate-y-1/2 w-12 h-12 bg-slate-100 rounded-full items-center justify-center z-10">
                <span className="text-2xl font-light text-slate-400">+</span>
              </div>
              
              <div className={`relative rounded-2xl overflow-hidden bg-slate-100 aspect-[9/16] transition-all duration-700 ${animationStep >= 1 ? 'opacity-100' : 'opacity-30'}`}>
                <motion.img
                  src={config.demo.dressImage}
                  alt="Selected Dress"
                  loading="lazy"
                  className="w-full h-full object-contain bg-slate-50"
                  initial={{ x: 50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.8 }}
                />
                <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/40 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/90 backdrop-blur rounded-full text-sm font-medium text-slate-700">
                    <ShoppingBag className="w-4 h-4 text-indigo-600" />
                    Selected Dress
                  </span>
                </div>
              </div>
              {animationStep === 1 && (
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="absolute -top-2 -right-2 w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center"
                >
                  <span className="text-white text-sm font-bold">2</span>
                </motion.div>
              )}
            </motion.div>

            {/* Output Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="relative"
            >
              {/* Arrow icon - hidden on mobile */}
              <div className="hidden md:flex absolute -left-8 top-1/2 -translate-y-1/2 w-12 h-12 bg-slate-100 rounded-full items-center justify-center z-10">
                <ArrowRight className="w-5 h-5 text-slate-400" />
              </div>
              
              <div className="relative rounded-2xl overflow-hidden bg-slate-100 aspect-[9/16]">
                <AnimatePresence mode="wait">
                  {animationStep === 2 ? (
                    <motion.div
                      key="processing"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50"
                    >
                      <div className="relative">
                        <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                        <Sparkles className="absolute inset-0 m-auto w-6 h-6 text-blue-600" />
                      </div>
                      <p className="mt-4 text-slate-600 font-medium">AI Try-On Processing</p>
                      <div className="mt-2 flex gap-1">
                        {[0, 1, 2].map((i) => (
                          <motion.div
                            key={i}
                            className="w-2 h-2 bg-blue-600 rounded-full"
                            animate={{ opacity: [0.3, 1, 0.3] }}
                            transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.2 }}
                          />
                        ))}
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="output"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: animationStep >= 3 ? 1 : 0.3 }}
                      className="w-full h-full"
                    >
                      <img
                        src={config.demo.outputImage}
                        alt="Try-On Result"
                        loading="lazy"
                        className="w-full h-full object-contain bg-slate-50"
                      />
                      <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/40 to-transparent" />
                      <div className="absolute bottom-4 left-4 right-4">
                        <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full text-sm font-medium">
                          <Sparkles className="w-4 h-4" />
                          Try-On Result
                        </span>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                {animationStep === 3 && (
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="absolute -top-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center"
                  >
                    <CheckCircle className="w-5 h-5 text-white" />
                  </motion.div>
                )}
              </div>
            </motion.div>
          </div>

          {/* Step indicators - Mobile */}
          <div className="flex justify-center gap-2 mt-8 md:hidden">
            {[0, 1, 2, 3].map((step) => (
              <div
                key={step}
                className={`w-2.5 h-2.5 rounded-full transition-colors ${
                  animationStep === step ? 'bg-blue-600' : 'bg-slate-200'
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Why Try-On Builds Confidence */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Why Virtual Try-On Changes Buying Decisions
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Eye, title: "See It On Yourself", desc: "Customers see the dress on their own body" },
              { icon: TrendingDown, title: "Reduce Guesswork", desc: "Reduces guesswork around fit & style" },
              { icon: ShoppingBag, title: "Fewer Returns", desc: 'Fewer "buy-and-return" purchases' },
              { icon: Zap, title: "Faster Checkout", desc: "Higher confidence → faster checkout" }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <div className="inline-flex items-center gap-3 px-6 py-4 bg-white rounded-2xl shadow-sm">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-green-600" />
              </div>
              <div className="text-left">
                <p className="text-slate-500 text-sm">Partners report up to</p>
                <p className="text-2xl font-bold text-slate-900">{config.stats.returnsReduction} fewer returns</p>
                <p className="text-slate-500 text-sm">when customers use Try-On before purchase</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* See It On You - Fit Confidence Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-4 leading-tight">
              See It On You.{" "}
              <span className="text-blue-600">Buy One Size, Confidently.</span>
            </h2>

            <p className="text-lg text-blue-700 font-semibold mb-4">
              &ldquo;Will this suit me?&rdquo;
            </p>
            
            <p className="text-slate-600 mb-6 max-w-2xl mx-auto">
              Virtual Try-On answers the most important question shoppers ask. 
              Photorealistic output with natural drape, optimized for Indian body types, 
              delivering studio-grade, PDP-ready images.
            </p>

            <div className="flex flex-wrap justify-center gap-3 mb-6">
              {[
                { label: 'Photorealistic output', icon: Eye },
                { label: 'Natural fabric drape', icon: Sparkles },
                { label: 'Indian body types', icon: Users },
                { label: 'Studio-grade quality', icon: Zap }
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-slate-700 bg-slate-50 px-3 py-2 rounded-lg">
                  <div className="w-6 h-6 rounded bg-blue-100 flex items-center justify-center">
                    <item.icon className="w-3.5 h-3.5 text-blue-600" />
                  </div>
                  {item.label}
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-4 sm:p-6 border border-blue-100"
          >
            <div className="relative rounded-xl overflow-hidden bg-white shadow-lg">
              <img 
                src="https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart-ai/20260117060449.webp"
                alt="AI Virtual Try-On demonstration showing realistic garment fitting on customer photo with natural drape"
                loading="lazy"
                className="w-full h-auto object-contain"
              />
            </div>
            <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="p-3 bg-blue-100/50 rounded-xl border border-blue-200 flex-1 text-center sm:text-left">
                <p className="text-sm text-blue-800">
                  <span className="font-semibold">Fit confidence before checkout.</span> When customers see themselves clearly, they buy with certainty.
                </p>
              </div>
              <p className="text-sm text-slate-600">
                <span className="font-semibold text-blue-600">Try-On answers:</span> &ldquo;Does this fit me?&rdquo;
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Not Just an Image Overlay */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium mb-4">
                <Shield className="w-4 h-4" />
                Advanced Technology
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Not a Filter. Not a Mannequin.
              </h2>
              <p className="text-slate-600 mb-8">
                NanoKart.ai uses body-aware garment warping and neural blending to produce realistic try-on outputs, avoiding the pasted or mannequin-like look common in basic solutions.
              </p>
              
              <div className="space-y-4">
                {[
                  { icon: Users, text: "Body-aware garment alignment" },
                  { icon: Palette, text: "Natural folds and edges" },
                  { icon: Eye, text: "Accurate colors & lighting" },
                  { icon: Smartphone, text: "Mobile-optimized outputs" }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                      <item.icon className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="text-slate-700">{item.text}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-blue-200/50">
                <img
                  src={config.demo.outputImage}
                  alt="Realistic Try-On Result"
                  loading="lazy"
                  className="w-full h-auto object-cover"
                />
              </div>
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full blur-2xl opacity-30" />
              <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full blur-2xl opacity-30" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who Is This For */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Who Is This For?
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Transform how your customers shop with virtual try-on technology
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { 
                icon: Store, 
                title: "Boutique Owners",
                problem: "Customers hesitate without trying",
                benefit: "Virtual try-on builds confidence"
              },
              { 
                icon: Smartphone, 
                title: "Social Sellers",
                problem: "Hard to showcase fit over chat",
                benefit: "Send personalized try-on previews"
              },
              { 
                icon: Building2, 
                title: "D2C Fashion Brands",
                problem: "High return rates hurt margins",
                benefit: "Visual confidence reduces returns"
              },
              { 
                icon: ShoppingBag, 
                title: "Marketplaces",
                problem: "Generic images don't convert",
                benefit: "Personalized experience drives sales"
              }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all group border border-slate-100"
              >
                {/* Header with icon */}
                <div className="p-6 pb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg shadow-blue-200">
                    <item.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-bold text-slate-900 text-lg">{item.title}</h3>
                </div>
                
                {/* Problem → Solution flow */}
                <div className="px-6 pb-6">
                  <div className="relative">
                    {/* Problem - subtle strikethrough style */}
                    <p className="text-slate-400 text-sm line-through decoration-slate-300 mb-2">
                      {item.problem}
                    </p>
                    {/* Arrow indicator */}
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex-1 h-px bg-gradient-to-r from-slate-200 to-blue-300"></div>
                      <ChevronRight className="w-4 h-4 text-blue-500" />
                      <div className="flex-1 h-px bg-gradient-to-r from-blue-300 to-blue-200"></div>
                    </div>
                    {/* Benefit - highlighted */}
                    <p className="text-slate-700 text-sm font-medium bg-gradient-to-r from-blue-50 to-indigo-50 px-3 py-2 rounded-lg border border-blue-100">
                      {item.benefit}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-24 bg-gradient-to-r from-blue-600 to-indigo-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Sparkles className="w-12 h-12 text-white/80 mx-auto mb-6" />
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-6">
              Let Customers Try Before They Buy
            </h2>
            <p className="text-white/80 mb-8 max-w-xl mx-auto">
              Start using Virtual Try-On today and see the difference in customer confidence and conversions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/partners/register"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-blue-600 font-semibold rounded-xl hover:bg-blue-50 transition-all"
              >
                Start Free
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/#pricing"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white font-semibold rounded-xl border border-white/20 hover:bg-white/20 transition-all"
              >
                View Pricing
                <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default TryOnPage;
